<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline */
import { computed, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TalkviaCustomDomainAPI from 'dashboard/api/talkviaCustomDomain';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const emit = defineEmits(['changed', 'loaded']);

const state = ref({
  available: false,
  cname_target: null,
  site_domain: null,
  domain: null,
});
const loading = ref(true);
const busy = ref(false);
const hostname = ref('');
const confirmingRemoval = ref(false);
const manageDialog = ref(null);

const domain = computed(() => state.value.domain);
const isActive = computed(() => domain.value?.status === 'active');
const statusLabel = computed(
  () =>
    ({
      active: 'Ativo',
      pending: 'Aguardando DNS',
      failed: 'Com erro',
    })[domain.value?.status] || ''
);
const statusClass = computed(
  () =>
    ({
      active: 'bg-n-teal-3 text-n-teal-11',
      pending: 'bg-n-amber-3 text-n-amber-11',
      failed: 'bg-n-ruby-3 text-n-ruby-11',
    })[domain.value?.status] || ''
);
const cnameTarget = computed(
  () => domain.value?.verification?.cname_target || state.value.cname_target
);

const errorMessage = error =>
  error?.response?.data?.error ||
  'Não foi possível concluir agora. Tente novamente em alguns minutos.';

const apply = (data, { notify = false } = {}) => {
  const wasActive = isActive.value;
  state.value = data;
  emit('loaded', data.domain);
  if (notify && isActive.value !== wasActive) emit('changed');
};

const load = async () => {
  loading.value = true;
  try {
    const { data } = await TalkviaCustomDomainAPI.show();
    apply(data);
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    loading.value = false;
  }
};

const run = async (action, successMessage) => {
  busy.value = true;
  try {
    const { data } = await action();
    apply(data, { notify: true });
    if (successMessage) useAlert(successMessage);
    return true;
  } catch (error) {
    useAlert(errorMessage(error));
    return false;
  } finally {
    busy.value = false;
  }
};

const openManager = () => {
  confirmingRemoval.value = false;
  manageDialog.value?.open();
};

const register = async () => {
  if (!hostname.value.trim()) return;
  const done = await run(
    () => TalkviaCustomDomainAPI.register(hostname.value.trim()),
    'Domínio cadastrado. Agora crie o CNAME no DNS da sua empresa.'
  );
  if (done) hostname.value = '';
};

const verify = () =>
  run(() => TalkviaCustomDomainAPI.verify(), 'Status do domínio atualizado.');

const confirmRemove = async () => {
  const done = await run(
    () => TalkviaCustomDomainAPI.remove(),
    'Domínio removido. Os links voltaram para o endereço da Talkvia.'
  );
  if (done) confirmingRemoval.value = false;
};

const copyTarget = async () => {
  try {
    await copyTextToClipboard(cnameTarget.value);
    useAlert('Destino do CNAME copiado.');
  } catch {
    useAlert('Não foi possível copiar. Selecione e copie o destino.');
  }
};

onMounted(load);
</script>

<template>
  <div
    class="mt-3 flex items-center gap-2 border-t border-n-weak pt-3"
    data-testid="custom-domain-summary"
  >
    <span
      class="i-hugeicons-globe-02 size-4 shrink-0 text-n-slate-10"
      aria-hidden="true"
    />
    <div class="min-w-0 flex-1">
      <p class="mb-0 text-xs text-n-slate-11">Domínio próprio</p>
      <p
        class="mb-0 truncate text-xs font-medium text-n-slate-12"
        :title="domain?.hostname"
      >
        {{
          loading
            ? 'Carregando…'
            : domain?.hostname || 'Usando o endereço da Talkvia'
        }}
      </p>
    </div>
    <span
      v-if="domain"
      class="shrink-0 rounded-full px-2 py-0.5 text-[11px]"
      :class="statusClass"
      >{{ statusLabel }}</span
    >
    <Button
      size="xs"
      color="slate"
      variant="faded"
      :disabled="loading"
      :label="domain ? 'Gerenciar' : 'Configurar'"
      @click="openManager"
    />
  </div>

  <Dialog
    ref="manageDialog"
    title="Domínio próprio"
    description="Use um endereço da sua empresa nos links das captações. Os links da Talkvia continuam funcionando."
    :show-confirm-button="false"
    cancel-button-label="Fechar"
    width="md"
  >
    <p v-if="!state.available && !domain" class="mb-0 text-sm text-n-slate-11">
      O domínio próprio ainda não está disponível nesta instalação. Fale com o
      suporte da Talkvia.
    </p>

    <div
      v-else-if="!domain && !state.site_domain"
      class="grid gap-2 text-sm text-n-slate-11"
    >
      <p class="mb-0">
        Para usar um domínio próprio, cadastre primeiro o site da empresa em
        Configurações da conta. Só subdomínios desse site poderão ser
        conectados.
      </p>
      <router-link
        :to="{ name: 'general_settings_index' }"
        class="font-medium text-n-brand"
      >
        Abrir Configurações da conta
      </router-link>
    </div>

    <form v-else-if="!domain" class="grid gap-2" @submit.prevent="register">
      <Input
        v-model="hostname"
        label="Subdomínio"
        :placeholder="`ofertas.${state.site_domain}`"
        :disabled="busy"
      />
      <p class="mb-0 text-xs text-n-slate-11">
        Use um subdomínio do site cadastrado da empresa, terminado em
        <strong>.{{ state.site_domain }}</strong
        >. O domínio principal não pode ser usado.
      </p>
      <div>
        <Button
          type="submit"
          size="sm"
          :is-loading="busy"
          :disabled="!hostname.trim()"
          label="Conectar domínio"
        />
      </div>
    </form>

    <div v-else class="grid gap-3">
      <div class="flex items-center justify-between gap-3">
        <p class="mb-0 break-all text-sm font-medium text-n-slate-12">
          {{ domain.hostname }}
        </p>
        <span
          class="shrink-0 rounded-full px-2 py-1 text-xs"
          :class="statusClass"
          >{{ statusLabel }}</span
        >
      </div>

      <div
        v-if="!isActive"
        class="grid gap-2 rounded-lg border border-n-weak bg-n-solid-2 p-3 text-xs text-n-slate-11"
      >
        <p class="mb-0 font-semibold text-n-slate-12">
          No DNS da sua empresa, crie este registro:
        </p>
        <dl class="mb-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
          <dt>Tipo</dt>
          <dd class="mb-0 font-mono text-n-slate-12">CNAME</dd>
          <dt>Nome</dt>
          <dd class="mb-0 break-all font-mono text-n-slate-12">
            {{ domain.hostname }}
          </dd>
          <dt>Destino</dt>
          <dd class="mb-0 flex min-w-0 items-center gap-1">
            <span class="break-all font-mono text-n-slate-12">{{
              cnameTarget
            }}</span>
            <Button
              icon="i-lucide-copy"
              size="xs"
              color="slate"
              variant="ghost"
              @click="copyTarget"
            />
          </dd>
        </dl>
        <p class="mb-0">
          Se o DNS da sua empresa estiver no Cloudflare, deixe o registro como
          “somente DNS” (nuvem cinza). A ativação costuma levar alguns minutos
          após a propagação.
        </p>
      </div>

      <p v-if="isActive" class="mb-0 text-sm text-n-slate-11">
        Os links das captações usam este domínio.
      </p>
      <p v-if="domain.error_message" class="mb-0 text-xs text-n-ruby-11">
        {{ domain.error_message }}
      </p>

      <div
        v-if="confirmingRemoval"
        class="grid gap-2 rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-3 text-sm text-n-ruby-12"
      >
        <p class="mb-0">
          Os links voltam para o endereço da Talkvia. Quem tiver o link com o
          seu domínio deixará de acessar a página.
        </p>
        <div class="flex flex-wrap gap-2">
          <Button
            size="sm"
            color="ruby"
            :is-loading="busy"
            label="Confirmar remoção"
            @click="confirmRemove"
          />
          <Button
            size="sm"
            color="slate"
            variant="faded"
            :disabled="busy"
            label="Cancelar"
            @click="confirmingRemoval = false"
          />
        </div>
      </div>

      <div v-else class="flex flex-wrap gap-2">
        <Button
          v-if="!isActive"
          size="sm"
          :is-loading="busy"
          label="Verificar agora"
          @click="verify"
        />
        <Button
          size="sm"
          color="ruby"
          variant="faded"
          :disabled="busy"
          label="Remover domínio"
          @click="confirmingRemoval = true"
        />
      </div>
    </div>
  </Dialog>
</template>
